import { Banner, Button, Input, Select, Surface, Switch, Table, Text } from '@cloudflare/kumo'
import {
  APP_NOTICE_TEXT_MAX,
  AUTO_PAYWALL_GAP_HOURS,
  AUTO_PAYWALL_LIMITS,
  appNoticeSettingsSchema,
  DEFAULT_AI_CONSENT_SETTINGS,
  appUpdateSettingsSchema,
  DEFAULT_APP_NOTICE_SETTINGS,
  DEFAULT_APP_SETTINGS,
  DEFAULT_APP_UPDATE_SETTINGS,
  DEFAULT_COST_SCENARIO_SETTINGS,
  DEFAULT_COURSE_TASTING_SETTINGS,
  DEFAULT_SAMPLE_BROKER_SETTINGS,
  DEFAULT_GENERATION_CANCEL_SETTINGS,
  DEFAULT_GENERATION_PACK_SETTINGS,
  DEFAULT_GENERATION_RESET_REMINDER_SETTINGS,
  DEFAULT_FAILED_PHOTO_RETRY_SETTINGS,
  DEFAULT_FORMULA_VERSIONS_SETTINGS,
  DEFAULT_FACTORY_REPLIES_SETTINGS,
  DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS,
  DEFAULT_GUEST_SIMULATION_LIMIT,
  DEFAULT_HEARD_FROM_SURVEY_SETTINGS,
  DEFAULT_MARKETING_CONSENT_SETTINGS,
  DEFAULT_PRODUCT_REVIEW_SETTINGS,
  DEFAULT_QUOTE_CANCEL_SETTINGS,
  DEFAULT_QUOTE_INTAKE_SETTINGS,
  DEFAULT_QUOTE_MARKETS_SETTINGS,
  DEFAULT_RESULT_UPSELL_SETTINGS,
  DEFAULT_REVISIT_NOTIFICATIONS_SETTINGS,
  DEFAULT_EXPERT_REVIEW_SETTINGS,
  DEFAULT_RESULT_BENCHMARKS_SETTINGS,
  DEFAULT_SAMPLE_CASES_SETTINGS,
  DEFAULT_SHARE_SCOPE_SETTINGS,
  DEFAULT_SOCIAL_SIGN_IN_SETTINGS,
  DEFAULT_SPEC_SHEET_SETTINGS,
  DEFAULT_START_QUESTIONS_SETTINGS,
  DEFAULT_STORE_URLS,
  DEFAULT_TASTE_AXES_SETTINGS,
  DEFAULT_SCALE_CHECK_SETTINGS,
  DEFAULT_SANDBOX_RIGHTS_SETTINGS,
  SANDBOX_RIGHTS_MODES,
  DEFAULT_QUOTE_STATUSES_SETTINGS,
  DEFAULT_LIVE_ACTIVITIES_SETTINGS,
  DEFAULT_HOME_WIDGETS_SETTINGS,
  DEFAULT_CERT_READINESS_SETTINGS,
  CERT_TARGETS_AVAILABLE,
  DEFAULT_TASTE_PREDICTION_SETTINGS,
  DEFAULT_UNSUPPORTED_CLAIMS_AUTO_REMOVE_SETTINGS,
  FORMULA_VERSION_KEEP,
  LANGUAGES,
  MARKETS,
  PLANS,
  productReviewThresholdsSchema,
  socialSignInSettingsSchema,
  TASTE_DETAIL_ACCESS,
  TASTE_TARGET_ACCESS,
  type AppNoticeSettings,
  type AppSettings,
  type AppUpdatePlatform,
  type AdminQuoteWaitlistSummary,
  type AppUpdateSettings,
  type GuestAbuseLimitsSettings,
  type Language,
  type Market,
  type Plan,
  type PlanQuota,
  type SampleCasesSettings,
  type SandboxRightsMode,
  type SocialSignInSettings,
} from '@repo/shared'
import { ArrowCounterClockwise, FloppyDisk } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { adminApi } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { useGetToken } from '../lib/auth'

type NumberKey = {
  [K in keyof PlanQuota]: PlanQuota[K] extends number ? K : never
}[keyof PlanQuota]
type BoolKey = {
  [K in keyof PlanQuota]: PlanQuota[K] extends boolean ? K : never
}[keyof PlanQuota]

/** 숫자 한도 행 — 라벨 · 보조 설명 · 최소 · 최대 */
const NUMBER_ROWS: { key: NumberKey; label: string; note: string; min: number; max: number }[] = [
  {
    key: 'generations',
    label: '제품 생성',
    note: '월 기준 · Free 는 평생 합계',
    min: 0,
    max: 100000,
  },
  {
    key: 'photos',
    label: '제품당 사진',
    note: '1 = 완성 사진만, 나머지는 잠금',
    min: 1,
    max: 3,
  },
  { key: 'turns', label: 'AI 수정', note: '월 기준 · Free 는 제품당', min: 0, max: 100000 },
  { key: 'regenerations', label: '사진 다시 생성', note: '월 기준', min: 0, max: 100000 },
  { key: 'competitors', label: '경쟁 제품 노출 수', note: '결과 화면', min: 0, max: 20 },
  {
    key: 'includedQuotes',
    label: '포함 견적 크레딧',
    note: '월 기준 · 90일 이월',
    min: 0,
    max: 100,
  },
]
const BOOL_ROWS: { key: BoolKey; label: string; note: string }[] = [
  { key: 'duplicate', label: '복사본 저장', note: '수정본을 새 제품으로 저장' },
  { key: 'badge', label: 'RecipeCat 배지', note: '공유 페이지·저장한 사진에 표시' },
  {
    key: 'specSheet',
    label: '제품 사양서',
    note: "결과를 한 장 그림으로 저장 · '제품 사양서' 스위치가 켜졌을 때만",
  },
]
/** 고르는 한도 행 (맛 고르기 세부 축·수치 목표). 값마다 관리자에게 보이는 이름 */
const CHOICE_ROWS = [
  {
    key: 'tasteDetail',
    label: '맛 세부 축',
    note: '만들기·결과 화면의 맛 카드',
    options: TASTE_DETAIL_ACCESS.map((value) => ({
      value,
      label: { preview: '흐린 미리보기', basic: '2~3축 3단계', full: '전부 5단계' }[value],
    })),
  },
  {
    key: 'tasteTargets',
    label: '맛 수치 목표',
    note: '염도 %, 당도 Brix 등',
    options: TASTE_TARGET_ACCESS.map((value) => ({
      value,
      label: { hidden: '안 보임', view: '보기만', edit: '직접 입력' }[value],
    })),
  },
] as const
const PLAN_LABEL: Record<Plan, string> = { free: 'Free', basic: 'Basic', pro: 'Pro' }
const UPDATE_PLATFORMS: { os: AppUpdatePlatform; label: string; store: string }[] = [
  { os: 'ios', label: 'iOS', store: 'App Store' },
  { os: 'android', label: 'Android', store: 'Google Play' },
]
type AppUpdateField = keyof AppUpdateSettings['ios']
type SocialProvider = keyof SocialSignInSettings['ios']
const SOCIAL_PROVIDERS: { key: SocialProvider; label: string }[] = [
  { key: 'apple', label: 'Apple 로그인' },
  { key: 'google', label: 'Google 로그인' },
]
type SampleCasesKey = keyof SampleCasesSettings
/** 완성 사례 갤러리 스위치 3개 (설계 6.8, 7장, 반박 검토 M2). 켜는 쪽은 저장 전에 확인 창을 거친다 */
const SAMPLE_CASES_ROWS: { key: SampleCasesKey; label: string; note: string; onNote: string }[] = [
  {
    key: 'enabled',
    label: '갤러리 보이기',
    note: '홈의 완성 사례 줄, 제품 시작 화면 셋째 카드, 글 상세의 시작 버튼을 보여요. 손님에게도 보여요.',
    onNote: '갤러리가 있는 새 앱 사용자 모두에게 보여요. 앱은 다음 실행 때 새 값을 읽어요.',
  },
  {
    key: 'startEnabled',
    label: '시작하기 허용 (모든 사례)',
    note: "끄면 모든 사례의 '이 사례로 시작하기' 버튼이 사라져요. 사례 상세와 글 버튼은 1~2분 안에, 제품 시작 화면 카드는 앱을 다시 열 때 바뀌어요. AI 비용이 갑자기 늘 때 쓰는 비상 스위치예요.",
    onNote: '사례로 제품을 만들 수 있어요. 시작 1번은 생성 1회를 쓰고 AI 비용이 들어요.',
  },
  {
    key: 'crossLanguageStart',
    label: '다른 언어 사례로 시작 허용',
    note: '끄면 앱 언어와 같은 언어의 사례만 시작할 수 있어요. 다른 언어 사례는 보기만 돼요. 개발 서버에서 언어가 다른 사례로 시작해 결과가 나오는지 확인한 뒤 켜세요.',
    onNote:
      '앱 언어와 다른 사례로도 시작해요. AI 가 결과를 앱 언어로 옮기는데, 검수에 걸리면 생성이 실패로 끝날 수 있어요.',
  },
]
/** 손님 남용 한도 (로드맵 P3). 0 = 끔. 기기 id 를 바꿔 보내는 무료 생성을 서버가 직접 보는 값으로 막는다 */
const GUEST_ABUSE_ROWS: {
  key: keyof GuestAbuseLimitsSettings
  label: string
  note: string
  max: number
}[] = [
  {
    key: 'perIpPerDay',
    label: '같은 IP 하루 제품 수',
    note: '회사·카페 공유기, 통신사 망 뒤의 여러 사람이 한 IP 로 보여 넉넉히 둬요. IPv6 는 같은 /64 를 한 곳으로 봐요',
    max: 10000,
  },
  {
    key: 'allGuestsPerHour',
    label: '모든 손님 1시간 제품 수',
    note: '여러 IP 로 나눠 오는 남용을 멈추는 비상 차단이에요. 평소 손님 수보다 크게 둬요',
    max: 100000,
  },
]
/** 견적 가능 시장 (로드맵 P7). 제품의 시장은 앱 언어로 정해진다(한국어 = kr, 그 밖 = us), Pro 는 직접 고른다 */
const MARKET_LABEL: Record<Market, string> = {
  kr: '한국 (kr · 한국어 앱 제품)',
  us: '해외 (us · 영어·일본어·중국어 앱 제품)',
}
const NOTICE_LANGUAGE_LABEL: Record<Language, string> = {
  ko: '한국어',
  en: '영어 (필수)',
  ja: '일본어',
  zh: '중국어',
}
/** 옛 API(guestAbuseLimits·appUpdate·socialSignIn·sampleCases·appNotice·tastePrediction·quoteIntake·startQuestions·marketingConsent·quoteMarkets·quoteCancel·refundRevoke·generationCancel·photoRetryFailed·heardFromSurvey·shareScope·revisitNotifications·costScenario·formulaVersions·aiConsent·factoryReplies·accessGrants·expertReview·generationPack·generationResetReminder·resultUpsell·specSheet·productReview·unsupportedClaimsAutoRemove·resultBenchmarks·sampleBroker·tasteAxes·scaleCheck·sandboxRights·quoteStatuses·liveActivities·homeWidgets·courseTasting·certReadiness 없음)가 돌려준 설정도 화면이 깨지지 않게 기본값을 채운다 */
const withDefaults = (s: AppSettings): AppSettings => ({
  ...s,
  manufacturing: s.manufacturing ?? DEFAULT_APP_SETTINGS.manufacturing,
  guestAbuseLimits: { ...DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS, ...s.guestAbuseLimits },
  appUpdate: s.appUpdate ?? DEFAULT_APP_UPDATE_SETTINGS,
  socialSignIn: s.socialSignIn ?? DEFAULT_SOCIAL_SIGN_IN_SETTINGS,
  // 칸이 하나 빠진 예전 값도 기본값으로 채운다 (다른 언어 시작 칸은 나중에 더했다)
  sampleCases: { ...DEFAULT_SAMPLE_CASES_SETTINGS, ...s.sampleCases },
  appNotice: s.appNotice ?? DEFAULT_APP_NOTICE_SETTINGS,
  tastePrediction: { ...DEFAULT_TASTE_PREDICTION_SETTINGS, ...s.tastePrediction },
  quoteIntake: { ...DEFAULT_QUOTE_INTAKE_SETTINGS, ...s.quoteIntake },
  startQuestions: { ...DEFAULT_START_QUESTIONS_SETTINGS, ...s.startQuestions },
  quoteCancel: { ...DEFAULT_QUOTE_CANCEL_SETTINGS, ...s.quoteCancel },
  refundRevoke: { ...DEFAULT_APP_SETTINGS.refundRevoke, ...s.refundRevoke },
  marketingConsent: { ...DEFAULT_MARKETING_CONSENT_SETTINGS, ...s.marketingConsent },
  quoteMarkets: { ...DEFAULT_QUOTE_MARKETS_SETTINGS, ...s.quoteMarkets },
  generationCancel: { ...DEFAULT_GENERATION_CANCEL_SETTINGS, ...s.generationCancel },
  photoRetryFailed: { ...DEFAULT_FAILED_PHOTO_RETRY_SETTINGS, ...s.photoRetryFailed },
  heardFromSurvey: { ...DEFAULT_HEARD_FROM_SURVEY_SETTINGS, ...s.heardFromSurvey },
  shareScope: { ...DEFAULT_SHARE_SCOPE_SETTINGS, ...s.shareScope },
  revisitNotifications: { ...DEFAULT_REVISIT_NOTIFICATIONS_SETTINGS, ...s.revisitNotifications },
  costScenario: { ...DEFAULT_COST_SCENARIO_SETTINGS, ...s.costScenario },
  formulaVersions: { ...DEFAULT_FORMULA_VERSIONS_SETTINGS, ...s.formulaVersions },
  accessGrants: { ...DEFAULT_APP_SETTINGS.accessGrants, ...s.accessGrants },
  aiConsent: { ...DEFAULT_AI_CONSENT_SETTINGS, ...s.aiConsent },
  factoryReplies: { ...DEFAULT_FACTORY_REPLIES_SETTINGS, ...s.factoryReplies },
  expertReview: { ...DEFAULT_EXPERT_REVIEW_SETTINGS, ...s.expertReview },
  generationPack: { ...DEFAULT_GENERATION_PACK_SETTINGS, ...s.generationPack },
  generationResetReminder: {
    ...DEFAULT_GENERATION_RESET_REMINDER_SETTINGS,
    ...s.generationResetReminder,
  },
  resultUpsell: { ...DEFAULT_RESULT_UPSELL_SETTINGS, ...s.resultUpsell },
  specSheet: { ...DEFAULT_SPEC_SHEET_SETTINGS, ...s.specSheet },
  productReview: {
    ...DEFAULT_PRODUCT_REVIEW_SETTINGS,
    ...s.productReview,
    thresholds: { ...DEFAULT_PRODUCT_REVIEW_SETTINGS.thresholds, ...s.productReview?.thresholds },
  },
  unsupportedClaimsAutoRemove: {
    ...DEFAULT_UNSUPPORTED_CLAIMS_AUTO_REMOVE_SETTINGS,
    ...s.unsupportedClaimsAutoRemove,
  },
  resultBenchmarks: { ...DEFAULT_RESULT_BENCHMARKS_SETTINGS, ...s.resultBenchmarks },
  b2bAds: { ...DEFAULT_APP_SETTINGS.b2bAds, ...s.b2bAds },
  sampleBroker: { ...DEFAULT_SAMPLE_BROKER_SETTINGS, ...s.sampleBroker },
  tasteAxes: { ...DEFAULT_TASTE_AXES_SETTINGS, ...s.tasteAxes },
  scaleCheck: { ...DEFAULT_SCALE_CHECK_SETTINGS, ...s.scaleCheck },
  sandboxRights: { ...DEFAULT_SANDBOX_RIGHTS_SETTINGS, ...s.sandboxRights },
  quoteStatuses: { ...DEFAULT_QUOTE_STATUSES_SETTINGS, ...s.quoteStatuses },
  liveActivities: { ...DEFAULT_LIVE_ACTIVITIES_SETTINGS, ...s.liveActivities },
  homeWidgets: { ...DEFAULT_HOME_WIDGETS_SETTINGS, ...s.homeWidgets },
  courseTasting: { ...DEFAULT_COURSE_TASTING_SETTINGS, ...s.courseTasting },
  certReadiness: { ...DEFAULT_CERT_READINESS_SETTINGS, ...s.certReadiness },
})
/** 테스트 결제 권리 정책 (settings.sandboxRights) */
const SANDBOX_RIGHTS_LABEL: Record<SandboxRightsMode, string> = {
  all: '모두 받음 (지금 동작)',
  allowlist: '허용 계정만',
  none: '받지 않음',
}
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))
/** 제품 검토 리포트 신호등 기준 (C45). 마진은 화면에서 % 로 받는다. 기본값은 @repo/shared PRODUCT_REVIEW_THRESHOLDS */
const PRODUCT_REVIEW_THRESHOLD_ROWS = [
  { key: 'marginRed', label: '마진 빨강 기준 (%)', note: '이 값 미만이면 빨강', percent: true },
  { key: 'marginYellow', label: '마진 노랑 기준 (%)', note: '이 값 미만이면 노랑', percent: true },
  {
    key: 'tastingRed',
    label: '시식 빨강 기준 (1~5)',
    note: '평균이 이 값 미만이면 빨강',
    percent: false,
  },
  {
    key: 'tastingGreen',
    label: '시식 초록 기준 (1~5)',
    note: '평균이 이 값 이상이면 초록',
    percent: false,
  },
] as const
const toMessage = (err: unknown) => (err instanceof Error ? err.message : String(err))

/** 앱 설정 — 게스트 한도, 플랜별 한도 매트릭스. 사용자별 플랜은 RevenueCat 이 정한다 (Users 페이지는 조회 전용) */
export function SettingsPage() {
  const getToken = useGetToken()
  const [saved, setSaved] = useState<AppSettings | null>(null)
  const [form, setForm] = useState<AppSettings>(DEFAULT_APP_SETTINGS)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  /** 시장별 견적 대기 명단 회원 수. 못 받으면(옛 API 등) null 로 두고 숫자 줄을 숨긴다 */
  const [waitlist, setWaitlist] = useState<AdminQuoteWaitlistSummary | null>(null)
  const [waitlistError, setWaitlistError] = useState(false)
  const reloadWaitlist = useCallback(async () => {
    setWaitlistError(false)
    try {
      setWaitlist(await adminApi.quoteWaitlist(getToken))
    } catch {
      setWaitlistError(true)
    }
  }, [getToken])

  useEffect(() => {
    adminApi
      .getSettings(getToken)
      .then(({ settings }) => {
        setSaved(withDefaults(settings))
        setForm(withDefaults(settings))
      })
      .catch((err: unknown) => setNotice({ kind: 'error', text: toMessage(err) }))
    void reloadWaitlist()
  }, [getToken, reloadWaitlist])

  const unlimitedGuest = form.guestSimulationLimit === 0
  const dirty = saved !== null && JSON.stringify(saved) !== JSON.stringify(form)
  // 새 버전 안내는 서버와 같은 스키마로 미리 검사한다 (형식 · 최소 ≤ 권장 · https 주소)
  const updateCheck = appUpdateSettingsSchema.safeParse(form.appUpdate)
  const updateError = (os: AppUpdatePlatform, field: AppUpdateField) => {
    if (updateCheck.success) return undefined
    const issue = updateCheck.error.issues.find((i) => i.path[0] === os && i.path[1] === field)
    if (!issue) return undefined
    return field === 'storeUrl' ? '비우거나 https:// 로 시작하는 주소를 넣으세요' : issue.message
  }

  // 간편 로그인도 서버와 같은 스키마로 미리 검사한다 (iOS 에서 Google 단독 금지 — 가이드라인 4.8)
  const socialCheck = socialSignInSettingsSchema.safeParse(form.socialSignIn)
  const socialError = socialCheck.success ? undefined : socialCheck.error.issues[0]?.message

  const setSocial = (os: AppUpdatePlatform, provider: SocialProvider, checked: boolean) =>
    setForm((f) => ({
      ...f,
      socialSignIn: {
        ...f.socialSignIn,
        [os]: { ...f.socialSignIn[os], [provider]: checked },
      },
    }))
  /**
   * iOS Google 스위치는 Apple 이 꺼져 있으면 켤 수 없다. 이미 켜진 채로 Apple 을 끈 경우에는 끌 수 있어야 하므로
   * 켜져 있을 때는 막지 않는다 (그 상태는 아래 오류 배너와 저장 버튼 잠금으로 막는다)
   */
  const socialDisabled = (os: AppUpdatePlatform, provider: SocialProvider) =>
    os === 'ios' &&
    provider === 'google' &&
    !form.socialSignIn.ios.apple &&
    !form.socialSignIn.ios.google

  const setUpdate = (os: AppUpdatePlatform, field: AppUpdateField, value: string) =>
    setForm((f) => ({
      ...f,
      appUpdate: { ...f.appUpdate, [os]: { ...f.appUpdate[os], [field]: value.trim() } },
    }))

  const setQuota = <K extends keyof PlanQuota>(plan: Plan, key: K, value: PlanQuota[K]) =>
    setForm((f) => ({
      ...f,
      planQuotas: { ...f.planQuotas, [plan]: { ...f.planQuotas[plan], [key]: value } },
    }))

  const setSampleCases = (key: SampleCasesKey, checked: boolean) =>
    setForm((f) => ({ ...f, sampleCases: { ...f.sampleCases, [key]: checked } }))
  /** 이번 저장에서 꺼짐에서 켜짐으로 바뀌는 완성 사례 스위치. 있으면 저장 전에 확인 창을 띄운다 (끄기는 바로 저장) */
  const sampleCasesTurningOn = saved
    ? SAMPLE_CASES_ROWS.filter((r) => !saved.sampleCases[r.key] && form.sampleCases[r.key])
    : []
  const [confirmOn, setConfirmOn] = useState(false)

  // 신호등 기준도 서버와 같은 스키마로 미리 검사한다 (빨강 기준이 노랑·초록 기준을 넘으면 저장이 거절된다)
  const thresholdCheck = productReviewThresholdsSchema.safeParse(form.productReview.thresholds)
  const thresholdError = thresholdCheck.success
    ? undefined
    : '빨강 기준은 노랑(마진)·초록(시식) 기준보다 클 수 없어요'

  // 공지 띠도 서버와 같은 스키마로 미리 검사한다 (켜면 영어 문구 필수 · https 주소 · 길이)
  const noticeCheck = appNoticeSettingsSchema.safeParse(form.appNotice)
  const noticeError = (path: string) =>
    noticeCheck.success
      ? undefined
      : noticeCheck.error.issues.find((i) => i.path.join('.') === path)?.message
  const setQuoteMarket = (market: Market, checked: boolean) =>
    setForm((f) => ({
      ...f,
      quoteMarkets: {
        ...f.quoteMarkets,
        markets: MARKETS.filter((m) =>
          m === market ? checked : f.quoteMarkets.markets.includes(m),
        ),
      },
    }))
  const waitlistUsers = (market: Market) =>
    waitlist?.markets.find((m) => m.market === market)?.users ?? 0

  const setAppNotice = (patch: Partial<AppNoticeSettings>) =>
    setForm((f) => ({ ...f, appNotice: { ...f.appNotice, ...patch } }))

  // 연타 잠금: busy 가 그려지기 전의 두 번째 클릭도 막는다
  const saving = useRef(false)
  const save = async () => {
    if (saving.current) return
    saving.current = true
    setBusy(true)
    setNotice(null)
    try {
      // 테스트 결제 허용 계정 칸은 쉼표로 나눠 받는다 — 입력 중 생긴 빈 칸은 저장 전에 지운다
      const { settings } = await adminApi.putSettings(getToken, {
        ...form,
        sandboxRights: {
          ...form.sandboxRights,
          allowedUserIds: form.sandboxRights.allowedUserIds.filter(Boolean),
        },
      })
      setSaved(withDefaults(settings))
      setForm(withDefaults(settings))
      setNotice({ kind: 'ok', text: '앱은 다음 실행 때 새 값을 읽어요.' })
    } catch (err) {
      setNotice({ kind: 'error', text: toMessage(err) })
    } finally {
      saving.current = false
      setBusy(false)
      setConfirmOn(false)
    }
  }
  const onSave = () => {
    if (sampleCasesTurningOn.length) setConfirmOn(true)
    else void save()
  }

  return (
    <div className="flex flex-col gap-8">
      {/* 페이지 헤더 + 저장 액션 (두 섹션이 한 설정 문서라 저장 버튼은 하나) */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Text variant="heading" size="lg">
            설정
          </Text>
          <Text variant="secondary">
            게스트 한도, 앱 버전 안내, 간편 로그인, 완성 사례, 플랜별 한도예요. 앱과 페이월이 이
            값을 <span className="font-mono text-sm">/config</span> 로 읽어 그대로 보여줘요.
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            icon={<ArrowCounterClockwise />}
            disabled={!dirty || busy}
            onClick={() => saved && setForm(saved)}
          >
            되돌리기
          </Button>
          <Button
            variant="primary"
            icon={<FloppyDisk />}
            loading={busy}
            disabled={
              !dirty ||
              saved === null ||
              !updateCheck.success ||
              !socialCheck.success ||
              !noticeCheck.success
            }
            onClick={onSave}
          >
            변경 저장
          </Button>
        </div>
      </div>

      <HelpPanel
        id="settings"
        steps={[
          <>
            숫자를 바꾸고 오른쪽 위 <b>변경 저장</b>을 누르세요. 저장 전에는 아무것도 바뀌지 않아요.
          </>,
          <>
            <b>플랜별 한도</b> 표는 가로가 플랜, 세로가 항목이에요. 페이월 문구의 숫자(월 15개 ·
            60개 등)도 이 값에서 나와요.
          </>,
        ]}
        notes={[
          '한도를 낮추면 이미 그보다 많이 쓴 사용자는 다음 초기화까지 더 만들 수 없어요.',
          '가격(월 $9.99 등)은 여기가 아니라 App Store Connect · Play Console 에서 바꿔요.',
          '누가 언제 바꿨는지는 [작업 기록] 에 남아요.',
          '앱 버전 안내의 최소 버전은 그 버전이 해당 스토어에 실제로 공개된 뒤에만 올리세요. 받을 수 없는 버전을 넣으면 그 플랫폼 사용자가 앱을 쓰지 못해요.',
          '간편 로그인은 Clerk 운영 환경에 Apple·Google 자격증명을 넣고 dev 에서 시험한 뒤 켜세요. 1.0.1 이상 앱이 다음 실행 때 읽어요.',
          '완성 사례 갤러리는 갤러리가 있는 새 앱이 스토어에 공개된 뒤에 켜세요. 켤 때는 확인 창이 한 번 더 떠요. 끄기는 바로 저장돼요.',
        ]}
      />

      {notice && (
        <Banner
          variant={notice.kind === 'ok' ? 'default' : 'error'}
          title={notice.kind === 'ok' ? '저장했어요' : '저장하지 못했어요'}
          description={notice.text}
        />
      )}
      {dirty && !notice && <Banner variant="alert" size="sm" title="저장하지 않은 변경이 있어요" />}

      {/* ── 게스트 ── */}
      <section className="flex flex-col gap-3">
        <div>
          <Text variant="heading">게스트 (비로그인)</Text>
          <Text variant="secondary" size="sm">
            가입을 요구하기 전에 한 기기가 만들 수 있는 제품 수예요. 저장한 값이 없으면 기본{' '}
            {DEFAULT_GUEST_SIMULATION_LIMIT}개예요. 실패한 생성은 세지 않아요. 기기 ID 는 앱이 만든
            값이라 바꿔 보내면 넘을 수 있어서, 아래 IP·전체 한도로 한 번 더 세요. 어느 한도에 걸려도
            앱은 가입 안내를 띄우고, 가입하면 바로 계속 만들 수 있어요. 0 이면 그 한도를 꺼요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <div className="flex flex-wrap items-end gap-6">
            <div className="w-56">
              <Input
                label="기기당 제품 수"
                description={unlimitedGuest ? '무제한' : '평생 합계, 서버에서 셈'}
                type="number"
                min={0}
                max={1000}
                value={String(form.guestSimulationLimit)}
                disabled={unlimitedGuest}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm({
                    ...form,
                    guestSimulationLimit: clamp(Number(e.target.value) || 0, 0, 1000),
                  })
                }
              />
            </div>
            <div className="pb-1">
              <Switch
                label="무제한"
                checked={unlimitedGuest}
                onCheckedChange={(checked: boolean) =>
                  setForm({
                    ...form,
                    guestSimulationLimit: checked ? 0 : DEFAULT_GUEST_SIMULATION_LIMIT,
                  })
                }
              />
            </div>
          </div>
          <div className="flex flex-wrap items-start gap-6">
            {GUEST_ABUSE_ROWS.map((row) => (
              <div key={row.key} className="w-72">
                <Input
                  label={row.label}
                  description={
                    form.guestAbuseLimits[row.key] === 0
                      ? `꺼짐 · 기본 ${DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS[row.key]}`
                      : row.note
                  }
                  type="number"
                  min={0}
                  max={row.max}
                  value={String(form.guestAbuseLimits[row.key])}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      guestAbuseLimits: {
                        ...form.guestAbuseLimits,
                        [row.key]: clamp(Number(e.target.value) || 0, 0, row.max),
                      },
                    })
                  }
                />
              </div>
            ))}
          </div>
        </Surface>
      </section>

      {/* ── 앱 버전 안내 (1.0.1~) ── */}
      <section className="flex flex-col gap-3">
        <div>
          <Text variant="heading">앱 버전 안내</Text>
          <Text variant="secondary" size="sm">
            앱이 시작할 때 자기 버전과 비교해요. 최소 버전보다 낮으면 닫을 수 없는 업데이트 화면,
            권장 버전보다 낮으면 닫을 수 있는 안내를 한 번 띄워요. 비워 두면 안내하지 않아요. 1.0.0
            앱은 이 안내를 모르고, 1.0.1 부터 동작해요.
          </Text>
        </div>
        <Banner
          variant="alert"
          size="sm"
          title="최소 버전은 그 버전이 해당 스토어에서 실제로 받을 수 있게 된 뒤에만 올리세요."
          description="iOS 와 Android 는 심사·공개 시점이 달라서 따로 정해요."
        />
        <Surface className="flex flex-col gap-6 rounded-lg p-6">
          {UPDATE_PLATFORMS.map(({ os, label, store }) => (
            <div key={os} className="flex flex-col gap-3">
              <span className="font-medium">{label}</span>
              <div className="flex flex-wrap items-start gap-6">
                <div className="w-40">
                  <Input
                    label="최소 버전"
                    description="미만이면 강제 업데이트"
                    placeholder="비우면 안내 없음"
                    value={form.appUpdate[os].minVersion}
                    error={updateError(os, 'minVersion')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUpdate(os, 'minVersion', e.target.value)
                    }
                  />
                </div>
                <div className="w-40">
                  <Input
                    label="권장 버전"
                    description="미만이면 안내 (닫기 가능)"
                    placeholder="비우면 안내 없음"
                    value={form.appUpdate[os].latestVersion}
                    error={updateError(os, 'latestVersion')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUpdate(os, 'latestVersion', e.target.value)
                    }
                  />
                </div>
                <div className="min-w-72 flex-1">
                  <Input
                    label={`${store} 주소`}
                    description="[업데이트] 버튼이 여는 곳. 비우면 기본 주소"
                    placeholder={DEFAULT_STORE_URLS[os]}
                    value={form.appUpdate[os].storeUrl}
                    error={updateError(os, 'storeUrl')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUpdate(os, 'storeUrl', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </Surface>
      </section>

      {/* ── 간편 로그인 (1.0.1~) ── */}
      <section className="flex flex-col gap-3">
        <div>
          <Text variant="heading">간편 로그인 (Apple·Google)</Text>
          <Text variant="secondary" size="sm">
            로그인 화면에 Apple·Google 버튼을 보일지 정해요. 끄면 이메일 코드 로그인만 보여요. 1.0.0
            앱은 이 값과 상관없이 항상 숨기고, 1.0.1 부터 동작해요.
          </Text>
        </div>
        <Banner
          variant="alert"
          size="sm"
          title="Clerk 운영 환경에 Apple·Google 자격증명을 넣은 뒤 켜세요."
          description="자격증명이 없으면 버튼을 눌러도 로그인이 실패해요. iOS 에서 Google 을 켜려면 Apple 도 같이 켜야 해요 (App Store 가이드라인 4.8)."
        />
        <Surface className="flex flex-col gap-6 rounded-lg p-6">
          {UPDATE_PLATFORMS.map(({ os, label }) => (
            <div key={os} className="flex flex-col gap-3">
              <span className="font-medium">{label}</span>
              <div className="flex flex-wrap items-center gap-8">
                {SOCIAL_PROVIDERS.map((p) => (
                  <Switch
                    key={p.key}
                    label={p.label}
                    checked={form.socialSignIn[os][p.key]}
                    disabled={socialDisabled(os, p.key)}
                    onCheckedChange={(checked: boolean) => setSocial(os, p.key, checked)}
                  />
                ))}
              </div>
            </div>
          ))}
          {socialError && (
            <Banner variant="error" size="sm" title="저장할 수 없어요" description={socialError} />
          )}
        </Surface>
      </section>

      {/* ── 완성 사례 갤러리 (2단계, 설계 6.8) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-sample-cases">
        <div>
          <Text variant="heading">완성 사례</Text>
          <Text variant="secondary" size="sm">
            앱의 완성 사례 갤러리를 보일지 정해요. 서버에 기능이 있고 이 스위치가 켜져야 앱이
            그려요. 옛 앱(1.0.4 이하)은 이 값을 모르고 바뀌는 것이 없어요. 사례마다 숨기기·순위는
            [완성 사례] 메뉴에서 해요.
          </Text>
        </div>
        <Banner
          variant="alert"
          size="sm"
          title="갤러리는 갤러리가 있는 새 앱이 스토어에 공개된 뒤에 켜세요."
          description="끄면 1~2분 안에 서버가 사례 목록을 닫아요(설정 전파와 응답 캐시가 각각 최대 1분). 앱 빌드는 필요 없어요."
        />
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          {SAMPLE_CASES_ROWS.map((row) => (
            <div key={row.key} className="flex flex-col gap-1">
              <Switch
                label={row.label}
                checked={form.sampleCases[row.key]}
                onCheckedChange={(checked: boolean) => setSampleCases(row.key, checked)}
              />
              <Text variant="secondary" size="sm">
                {row.note}
              </Text>
            </div>
          ))}
        </Surface>
      </section>

      {/* Internal administrator copy follows the existing Korean settings UI. */}
      <section className="flex flex-col gap-3">
        <Text variant="heading">제조 협력 접수</Text>
        <Text variant="secondary">
          앱과 공장 포털의 제조 요청을 연결합니다. 서버의 기능 준비와 PC 처리기 연결을 먼저
          확인하세요. 끄면 새 작업이 멈추며 기존 제조 요청은 기존 견적 경로에서 바뀌지 않습니다.
        </Text>
        <Surface className="rounded-lg p-6">
          <Switch
            label="제조 협력 접수·공장 포털 활성화"
            checked={form.manufacturing.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, manufacturing: { enabled: checked } }))
            }
          />
        </Surface>
      </section>
      {/* ── AI 맛 예측 카드 (2026-10-25) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-taste-prediction">
        <div>
          <Text variant="heading">AI 맛 예측</Text>
          <Text variant="secondary" size="sm">
            결과 화면에 레시피의 원료·배합·공정으로 추정한 맛·향·식감 점수를 보여요. 카드에는 'AI
            추정(시식 전)' 표시와 근거 한 줄이 늘 붙어요. 제품 판마다 AI 를 한 번 불러요(생성과 같은
            Claude). 끄면 서버가 새로 추정하지 않고 앱도 카드를 그리지 않아요. 카드가 있는 새 앱만
            읽고, 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="결과 화면에 AI 맛 예측 보이기"
            checked={form.tastePrediction.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, tastePrediction: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 견적 접수 (C36) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-quote-intake">
        <div>
          <Text variant="heading">견적 접수</Text>
          <Text variant="secondary" size="sm">
            공장이나 처리 인원이 모자랄 때 새 견적 요청을 멈춰요. 끄면 새 앱은 결제 전에 '지금은
            견적 요청을 받지 않아요' 안내를 보이고 결제창을 열지 않아요. 이 스위치를 모르는 옛 앱은
            결제한 뒤에 요청을 보내서, 꺼도 서버가 받아요(막으면 돈만 나가요). 앱은 다음 실행 때 새
            값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="새 견적 요청 받기"
            checked={form.quoteIntake.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, quoteIntake: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 견적 가능 시장 (로드맵 P7) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-quote-markets">
        <div>
          <Text variant="heading">견적 가능 시장</Text>
          <Text variant="secondary" size="sm">
            응대할 공장이 있는 시장의 제품만 견적을 받아요. 고르지 않은 시장의 제품은 새 앱에서
            결제창 대신 지원하지 않는 지역이라는 안내와 대기 명단 등록 버튼이 떠요. 시장을 모르는 옛
            앱은 결제한 뒤 요청을 보내는데, 서버가 견적권을 쓰기 전에 거절해서 산 견적권은
            남아요(환불은 스토어에서). 새 앱은 견적 진입·결제 전에 설정을 다시 확인해요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          {waitlistError && (
            <Banner
              variant="alert"
              title="대기 명단 집계를 불러오지 못했어요"
              description="표시된 수가 있으면 마지막 조회 값이에요. 다시 조회해 주세요."
            />
          )}
          <Button variant="ghost" size="sm" onClick={() => void reloadWaitlist()}>
            대기 명단 새로고침
          </Button>
          <Switch
            label="시장 제한 켜기 (끄면 모든 시장 제품이 견적을 받아요)"
            checked={form.quoteMarkets.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, quoteMarkets: { ...f.quoteMarkets, enabled: checked } }))
            }
          />
          <div className="flex flex-col gap-3">
            {MARKETS.map((market) => (
              <div key={market} className="flex flex-wrap items-center gap-4">
                <Switch
                  label={MARKET_LABEL[market]}
                  checked={form.quoteMarkets.markets.includes(market)}
                  disabled={!form.quoteMarkets.enabled}
                  onCheckedChange={(checked: boolean) => setQuoteMarket(market, checked)}
                />
                {waitlist && (
                  <Text variant="secondary" size="sm">
                    대기 명단 {waitlistUsers(market).toLocaleString()}명
                  </Text>
                )}
              </div>
            ))}
          </div>
        </Surface>
      </section>

      {/* ── 아이디어 시작 질문 (C39) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-start-questions">
        <div>
          <Text variant="heading">아이디어 시작 질문</Text>
          <Text variant="secondary" size="sm">
            아이디어로 만들 때 설명 화면에서 사용 목적·목표 수량·예산·판매 대상을 물어요. 모두
            건너뛸 수 있고, 고른 답은 생성 지시문에 들어가요. 기본은 꺼짐이에요(선택지 확인 전). 이
            질문을 모르는 옛 앱에는 보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="시작 질문 보이기"
            checked={form.startQuestions.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, startQuestions: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 견적 취소·견적권 반환 (C48) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-quote-cancel">
        <div>
          <Text variant="heading">견적 취소·견적권 반환</Text>
          <Text variant="secondary" size="sm">
            취소 버튼을 켜면 고객이 공장 제안이 아직 없는 접수 상태 견적을 앱에서 취소하고, 쓴
            견적권을 돌려받아요(구독 포함 견적권은 원래 만료일 그대로). 이 버튼을 모르는 옛 앱에는
            보이지 않아요. 0건 마감 반환은 제안 0건인 견적을 마감할 때 견적권을 자동으로 돌려줘요.
            돌려준 견적과 고객이 취소한 견적은 다시 열 수 없어요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="앱에서 견적 취소 허용"
            checked={form.quoteCancel.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, quoteCancel: { ...f.quoteCancel, enabled: checked } }))
            }
          />
          <Switch
            label="제안 0건 마감 때 견적권 자동 반환"
            checked={form.quoteCancel.refundNoOfferClose}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({
                ...f,
                quoteCancel: { ...f.quoteCancel, refundNoOfferClose: checked },
              }))
            }
          />
        </Surface>
      </section>

      <section className="flex flex-col gap-3">
        <Text variant="heading">스토어 환불 권리 처리</Text>
        <Surface className="flex flex-col gap-3 rounded-lg p-6">
          <Switch
            label="확인된 전액 환불의 미사용 권리 자동 회수"
            checked={form.refundRevoke.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, refundRevoke: { enabled } }))
            }
          />
          <Text variant="secondary" size="sm">
            이미 사용한 견적권은 회수하지 않아요. 생성 팩은 남은 횟수만 회수하고 사용분은 확인
            대상으로 표시해요. 부분 환불이나 거래·금액을 확인할 수 없는 건은 사용자 화면의 결제·환불
            기록에서 확인하세요. 꺼도 원장 기록은 유지해요.
          </Text>
        </Surface>
      </section>

      {/* ── 테스트 결제 권리 (Top10 #4, 대표 결정 G 대기) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-sandbox-rights">
        <Text variant="heading">테스트 결제 권리</Text>
        <Surface className="flex flex-col gap-3 rounded-lg p-6">
          <Select
            label="샌드박스·테스트 모드 결제가 권리를 받는 범위"
            className="max-w-md"
            value={form.sandboxRights.mode}
            onValueChange={(v: string | null) => {
              const mode = SANDBOX_RIGHTS_MODES.find((m) => m === v)
              if (mode) setForm((f) => ({ ...f, sandboxRights: { ...f.sandboxRights, mode } }))
            }}
            items={SANDBOX_RIGHTS_MODES.map((value) => ({
              value,
              label: SANDBOX_RIGHTS_LABEL[value],
            }))}
          />
          <Input
            label="허용 계정 (사용자 id, 쉼표로 구분)"
            description="'허용 계정만'일 때 이 계정의 테스트 결제만 권리를 받아요. Apple 심사 계정은 꼭 넣어요."
            placeholder="user_..., user_..."
            disabled={form.sandboxRights.mode !== 'allowlist'}
            value={form.sandboxRights.allowedUserIds.join(', ')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const allowedUserIds = e.target.value.split(',').map((id) => id.trim())
              setForm((f) => ({ ...f, sandboxRights: { ...f.sandboxRights, allowedUserIds } }))
            }}
          />
          <Text variant="secondary" size="sm">
            TestFlight·앱 심사·라이선스 테스터 결제와 Stripe 테스트 모드 결제에 적용해요. 막힌
            계정은 다음 결제 동기화 때 테스트 구독이 무료로 보이고, 새 테스트
            구매(견적권·검수권·생성 팩)는 지급하지 않아요. 이미 지급한 것은 그대로예요. 결제·환불
            기록은 정책과 상관없이 남아요.
          </Text>
        </Surface>
      </section>

      {/* ── 광고성 알림 수신 동의 (로드맵 P6) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-marketing-consent">
        <div>
          <Text variant="heading">광고성 알림 수신 동의</Text>
          <Text variant="secondary" size="sm">
            새 앱이 회원·손님마다 한 번 '혜택·이벤트 알림'을 받을지 묻고, '동의'와 '동의 안 함' 중
            하나를 꼭 고르게 해요(어느 쪽이든 앱은 그대로 쓸 수 있어요). 법 준수 기능이라 기본은
            켬이에요. 꺼도 업셀·가입 유도·광고 공지 푸시는 동의한 사람에게만 가고, 계정 탭의 수신
            토글은 그대로 보여요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="처음 실행할 때 수신 동의 선택 화면 보이기"
            checked={form.marketingConsent.promptEnabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, marketingConsent: { promptEnabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 생성 취소 (C50) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-generation-cancel">
        <div>
          <Text variant="heading">생성 취소</Text>
          <Text variant="secondary" size="sm">
            생성을 기다리는 화면에 '취소' 버튼을 보여요. 순서를 기다리는 중에 취소하면 생성 사용권을
            돌려주고, AI 가 만들기 시작한 뒤 취소하면 돌려주지 않아요(확인 창에 적혀 있어요). 끄면
            서버도 취소 요청을 받지 않아요. 취소를 모르는 옛 앱에는 취소한 생성이 실패로 보여요.
            앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="생성 취소 버튼 보이기"
            checked={form.generationCancel.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, generationCancel: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 실패한 사진 다시 만들기 (C49) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-failed-photo-retry">
        <Text variant="heading">실패한 사진 다시 만들기</Text>
        <Text variant="secondary" size="sm">
          성공한 사진은 그대로 두고 실패한 컷만 다시 만들어요. 같은 결과에서 첫 재시도는 무료이고,
          이후에는 기존 사진 재생성 한도를 사용해요. 끄면 서버도 요청을 받지 않아요.
        </Text>
        <Surface className="rounded-lg p-6">
          <Switch
            label="실패 사진 재시도 버튼 보이기"
            checked={form.photoRetryFailed.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, photoRetryFailed: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── 어떻게 알고 오셨어요? (C56) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-heard-from">
        <div>
          <Text variant="heading">어떻게 알고 오셨어요?</Text>
          <Text variant="secondary" size="sm">
            이 기기에서 처음 완성된 결과 아래에 한 번, 견적 요청서에는 선택 칸으로 물어요. 건너뛸 수
            있고, 답은 사용 분석과 견적 상세에 남아요(AI 답변·검색·SNS·지인 추천·행사 또는
            메일·기타). 기본은 꺼짐이에요. 이 질문을 모르는 옛 앱에는 보이지 않아요. 앱은 다음 실행
            때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="질문 보이기"
            checked={form.heardFromSurvey.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, heardFromSurvey: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 웹 공유 공개 범위 (C54) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-share-scope">
        <div>
          <Text variant="heading">웹 공유 공개 범위</Text>
          <Text variant="secondary" size="sm">
            결과를 웹 링크로 공유할 때 링크마다 '공개 모드(배합·원가 포함)'와 '비공개
            모드(사진·이름·설명만)'를 고르게 해요. 이미 만든 링크도 고르는 즉시 바뀌어요. 새 링크의
            기본은 비공개 모드예요(2026-10-30, 스위치와 상관없음). 꺼져 있으면 선택이 보이지 않고 새
            링크는 모두 비공개 모드, 이미 만든 링크는 지금 모드 그대로 열려요. 옛 앱에는 선택이
            보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="공개·비공개 모드 선택 보이기"
            checked={form.shareScope.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, shareScope: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 결과 화면 원가 시나리오 (C46) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-cost-scenario">
        <div>
          <Text variant="heading">원가 시나리오</Text>
          <Text variant="secondary" size="sm">
            결과 화면 원가 칸의 '자세히' 안에 수율·충전량·포장비·판매 채널 수수료·세율을 바꿔 낱개
            원가와 마진을 다시 계산해 보는 접힌 칸을 보여요. 앱 안에서만 계산하는 예시 계산이고
            저장하지 않아요. 기본은 꺼짐이에요. 이 칸을 모르는 옛 앱에는 보이지 않아요. 앱은 다음
            실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="원가 시나리오 보이기"
            checked={form.costScenario.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, costScenario: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 배합 버전 이력 (C42) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-formula-versions">
        <div>
          <Text variant="heading">배합 버전 기록 메뉴</Text>
          <Text variant="secondary" size="sm">
            결과 화면 메뉴에 '버전 기록'(두 버전 비교·예전 버전으로 되돌리기)과 견적 상세의 '이전
            기준' 안내를 보여요. 버전 자체는 이 스위치와 상관없이 덮어쓰기 저장·이름 바꾸기마다
            남아요 — 견적과 샘플 주문이 그때의 배합을 가리켜야 해서요. 제품마다 최근{' '}
            {FORMULA_VERSION_KEEP}개까지 남기고, 견적·샘플 주문·검토 리포트가 가리키는 버전은 그보다
            오래돼도 남겨요. 버전은 제품이 파기될 때 함께 지워져요. 이 메뉴를 모르는 옛 앱에는
            보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="버전 기록 메뉴 켜기"
            checked={form.formulaVersions.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, formulaVersions: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 공장 회신 받기 (로드맵 8번) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-factory-replies">
        <div>
          <Text variant="heading">공장 회신 받기</Text>
          <Text variant="secondary" size="sm">
            공장 목록에서 공장별 초대 링크를 만들어, 공장이 설비·공정별 단가·최소 주문량을 웹 폼으로
            보내게 해요. 같은 틀의 CSV 양식 내려받기·가져오기도 열려요. 받은 회신은 관리자가
            '반영'을 눌러야 공장 목록에 들어가요. 기본은 꺼짐이고, 끄면 이미 보낸 초대 링크도 바로
            닫혀요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="공장 회신 받기 켜기"
            checked={form.factoryReplies.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, factoryReplies: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 제품 검토 리포트 (C45) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-product-review">
        <div>
          <Text variant="heading">제품 검토 리포트</Text>
          <Text variant="secondary" size="sm">
            결과 화면에 '제품 검토 리포트' 줄을 보여요. 누르면 맛·원가·판매 문구·보관·생산 준비 다섯
            영역을 신호등으로, 고칠 것 3개를 먼저 보여요. AI 를 새로 부르지 않고 저장된 결과·AI 맛
            추정·시식 기록·견적 단계만 모아요. 판매 문구의 효능·인증 단정·확률·맛 점수 같은 근거
            없는 표현을 표시해요. 만든 리포트는 고치지 않고 제품마다 저장하며 제품이 파기될 때 함께
            지워져요. 기본은 꺼짐이고, 이 줄을 모르는 옛 앱에는 보이지 않아요. 앱은 다음 실행 때 새
            값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="제품 검토 리포트 켜기"
            checked={form.productReview.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, productReview: { ...f.productReview, enabled: checked } }))
            }
          />
          <div className="flex flex-wrap items-start gap-6">
            {PRODUCT_REVIEW_THRESHOLD_ROWS.map((row) => (
              <div key={row.key} className="w-56">
                <Input
                  label={row.label}
                  description={row.note}
                  type="number"
                  step={row.percent ? 1 : 0.1}
                  value={String(
                    row.percent
                      ? Math.round(form.productReview.thresholds[row.key] * 100)
                      : form.productReview.thresholds[row.key],
                  )}
                  error={row === PRODUCT_REVIEW_THRESHOLD_ROWS[0] ? thresholdError : undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const n = Number(e.target.value) || 0
                    setForm((f) => ({
                      ...f,
                      productReview: {
                        ...f.productReview,
                        thresholds: {
                          ...f.productReview.thresholds,
                          [row.key]: row.percent ? clamp(n, -100, 100) / 100 : clamp(n, 1, 5),
                        },
                      },
                    }))
                  }}
                />
              </div>
            ))}
            <div className="w-56">
              <Input
                label="제품마다 남기는 리포트 수"
                description={`기본 ${DEFAULT_PRODUCT_REVIEW_SETTINGS.keep}개`}
                type="number"
                min={1}
                max={100}
                value={String(form.productReview.keep)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    productReview: {
                      ...f.productReview,
                      keep: clamp(Math.round(Number(e.target.value) || 1), 1, 100),
                    },
                  }))
                }
              />
            </div>
          </div>
        </Surface>
      </section>

      {/* ── 판매 문구 자동 정리 (C45) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-unsupported-claims">
        <div>
          <Text variant="heading">판매 문구 자동 정리</Text>
          <Text variant="secondary" size="sm">
            새로 만든 결과와 AI 수정 결과의 제품명·한 줄 소개·먹는 법·만드는 법에서 인증 단정(HACCP
            적합)·확률 단정(출시 성공 확률 80%)·근거 없는 맛 점수(단맛 8)를 효능·질병 표현처럼
            자동으로 빼요. 뺀 표현은 제품 검토 리포트의 '판매 문구'에 남아요. 기본은
            켜짐이에요(식품표시광고법 제8조 허위·과장 표시 방어). 끄면 이 세 가지는 빼지 않고
            리포트에서 표시만 해요. 효능·질병 표현은 이 스위치와 상관없이 늘 빼요. 이미 만든 결과는
            바뀌지 않아요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="인증·확률·맛 점수 표현 자동으로 빼기"
            checked={form.unsupportedClaimsAutoRemove.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, unsupportedClaimsAutoRemove: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 결과 화면 비교 블록 (2026-10-30) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-result-benchmarks">
        <div>
          <Text variant="heading">시판 제품 가격 비교</Text>
          <Text variant="secondary" size="sm">
            결과 화면 '지금 팔리는 제품' 칸 아래에 시판 제품과 내 제품을 100 g(ml) 당 가격으로 맞춰
            보여요. 경쟁 제품처럼 플랜만큼만 보이고, 다른 회사 상품 링크는 걸지 않아요. 기본은
            꺼짐이에요. 이 칸을 모르는 옛 앱에는 보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="시판 제품 100 g 당 가격 비교 보이기"
            checked={form.resultBenchmarks.priceCompare}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, resultBenchmarks: { priceCompare: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── AI 중계 샘플 거래 ── */}
      <section className="flex flex-col gap-3" data-testid="settings-sample-broker">
        <div>
          <Text variant="heading">샘플 거래</Text>
          <Text variant="secondary" size="sm">
            결과 화면에 '샘플 받기'를 보여요 (그 제품 분류에 샘플 가능 공장이 있을 때만). 고객은
            결제 뒤에 공장과 서로 공개되고, 공장 포털에 샘플 메뉴가 열려요. 기본은 꺼짐이에요. 무료
            방식(무료 샘플·정보 제공·무료 시식)은 아래 연결 수수료를 받고, 비워 두면 그 통화의 무료
            방식은 고를 수 없어요. 결제는 Stripe 키가 있어야 받아요(없으면 운영은 결제를 시작하지
            못해요). 공장 수수료율·정산은 공장 계약이 정해진 뒤에 써요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="샘플 거래 켜기"
            checked={form.sampleBroker.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, sampleBroker: { ...f.sampleBroker, enabled: checked } }))
            }
          />
          <Switch
            label="AI 중계 대화 켜기 (공장 메시지·역제안을 AI 가 고객 조건에 맞춰 정리해 전달)"
            checked={form.sampleBroker.messagesEnabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({
                ...f,
                sampleBroker: { ...f.sampleBroker, messagesEnabled: checked },
              }))
            }
          />
          {(['KRW', 'USD'] as const).map((currency) => (
            <Input
              key={currency}
              label={`연결 수수료 ${currency} (비우면 그 통화의 무료 방식 꺼짐)`}
              type="number"
              min={0}
              value={String(form.sampleBroker.connectionFee[currency] ?? '')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value)
                setForm((f) => ({
                  ...f,
                  sampleBroker: {
                    ...f.sampleBroker,
                    connectionFee: {
                      ...f.sampleBroker.connectionFee,
                      [currency]: e.target.value && value > 0 ? value : null,
                    },
                  },
                }))
              }}
            />
          ))}
        </Surface>
      </section>

      {/* ── 플랜 한도 매트릭스 ── */}
      <section className="flex flex-col gap-3">
        <div>
          <Text variant="heading">플랜별 한도</Text>
          <Text variant="secondary" size="sm">
            월 한도는 구독을 시작한 날짜 기준으로 매달 초기화돼요. Free 는 평생 합계예요.
          </Text>
        </div>
        <Surface className="overflow-x-auto rounded-lg p-0">
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head className="w-48">항목</Table.Head>
                {PLANS.map((plan) => (
                  <Table.Head key={plan}>{PLAN_LABEL[plan]}</Table.Head>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {NUMBER_ROWS.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="font-medium">{row.label}</span>
                      <span className="text-kumo-subtle text-xs">{row.note}</span>
                    </div>
                  </Table.Cell>
                  {PLANS.map((plan) => (
                    <Table.Cell key={plan}>
                      <div className="w-24">
                        <Input
                          aria-label={`${PLAN_LABEL[plan]} ${row.label}`}
                          type="number"
                          min={row.min}
                          max={row.max}
                          value={String(form.planQuotas[plan][row.key])}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setQuota(
                              plan,
                              row.key,
                              clamp(Number(e.target.value) || 0, row.min, row.max),
                            )
                          }
                        />
                      </div>
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
              {CHOICE_ROWS.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="font-medium">{row.label}</span>
                      <span className="text-kumo-subtle text-xs">{row.note}</span>
                    </div>
                  </Table.Cell>
                  {PLANS.map((plan) => (
                    <Table.Cell key={plan}>
                      <Select
                        aria-label={`${PLAN_LABEL[plan]} ${row.label}`}
                        size="sm"
                        className="min-w-32"
                        value={form.planQuotas[plan][row.key]}
                        onValueChange={(v: string | null) => {
                          const picked = row.options.find((o) => o.value === v)
                          if (picked)
                            setQuota(plan, row.key, picked.value as PlanQuota[typeof row.key])
                        }}
                        items={[...row.options]}
                      />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
              {BOOL_ROWS.map((row) => (
                <Table.Row key={row.key}>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="font-medium">{row.label}</span>
                      <span className="text-kumo-subtle text-xs">{row.note}</span>
                    </div>
                  </Table.Cell>
                  {PLANS.map((plan) => (
                    <Table.Cell key={plan}>
                      <Switch
                        aria-label={`${PLAN_LABEL[plan]} ${row.label}`}
                        checked={form.planQuotas[plan][row.key]}
                        onCheckedChange={(checked: boolean) => setQuota(plan, row.key, checked)}
                      />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Surface>

        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <div className="w-56">
            <Input
              label="체험 중 제품 수"
              description="7일 체험 동안의 생성 상한이에요. 다른 한도는 그 플랜 값 그대로예요."
              type="number"
              min={0}
              max={100000}
              value={String(form.planQuotas.trialGenerations)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm({
                  ...form,
                  planQuotas: {
                    ...form.planQuotas,
                    trialGenerations: clamp(Number(e.target.value) || 0, 0, 100000),
                  },
                })
              }
            />
          </div>
        </Surface>
      </section>

      {/* ── 홈 공지 띠 (C20) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-app-notice">
        <div>
          <Text variant="heading">홈 공지 띠</Text>
          <Text variant="secondary" size="sm">
            앱 홈 맨 위에 띠를 띄워 장애·점검·정책 변경을 알려요. 손님과 푸시를 끈 회원에게도
            보여요. 앱 언어 문구가 비면 영어 문구를 보여요. 띠가 있는 새 앱만 읽고, 옛 앱(1.0.5
            이하)은 바뀌는 것이 없어요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <div className="flex flex-wrap items-center gap-8">
            <Switch
              label="띠 보이기"
              checked={form.appNotice.enabled}
              onCheckedChange={(checked: boolean) => setAppNotice({ enabled: checked })}
            />
            <Switch
              label="경고 색 (장애·점검)"
              checked={form.appNotice.level === 'warning'}
              onCheckedChange={(checked: boolean) =>
                setAppNotice({ level: checked ? 'warning' : 'info' })
              }
            />
          </div>
          {LANGUAGES.map((lang) => (
            <Input
              key={lang}
              label={NOTICE_LANGUAGE_LABEL[lang]}
              maxLength={APP_NOTICE_TEXT_MAX}
              value={form.appNotice.text[lang]}
              error={noticeError(`text.${lang}`)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAppNotice({ text: { ...form.appNotice.text, [lang]: e.target.value } })
              }
            />
          ))}
          <Input
            label="링크 (선택)"
            description="띠를 누르면 여는 주소. 비우면 누를 수 없는 띠"
            placeholder="https://"
            value={form.appNotice.url}
            error={noticeError('url') && '비우거나 https:// 로 시작하는 주소를 넣으세요'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAppNotice({ url: e.target.value.trim() })
            }
          />
        </Surface>
      </section>

      {/* ── 재방문 알림 (2026-10-29) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-revisit-notifications">
        <div>
          <Text variant="heading">재방문 알림</Text>
          <Text variant="secondary" size="sm">
            매시 서버가 상태를 보고 푸시와 알림 탭에 남겨요. 한도 리마인더는 무료 생성을 다 쓰고
            아직 플랜을 올리지 않은 회원에게 하루·일주일 뒤, 견적 유도는 완성한 제품에 견적 요청이
            없으면 하루 뒤 한 번 보내요. 둘 다 광고라 혜택 알림 수신에 동의한 회원에게만, 한국과
            현지 모두 낮(08~21시)에만 '(광고)' 표기를 붙여 가요. 한 사람에게 하루 한 건, 종류마다
            30일에 두 건까지예요. 앱 업데이트 없이 다음 정각부터 적용돼요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="한도 리마인더 보내기"
            checked={form.revisitNotifications.limitReminder}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({
                ...f,
                revisitNotifications: { ...f.revisitNotifications, limitReminder: checked },
              }))
            }
          />
          <Switch
            label="견적 유도 보내기"
            checked={form.revisitNotifications.quoteNudge}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({
                ...f,
                revisitNotifications: { ...f.revisitNotifications, quoteNudge: checked },
              }))
            }
          />
        </Surface>
      </section>

      {/* ── 배정 운영자 접근 (C52) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-access-grants">
        <div>
          <Text variant="heading">배정 운영자 접근</Text>
          <Text variant="secondary" size="sm">
            관리자가 아닌 운영자에게 견적을 하나씩 맡겨, 맡은 견적만 보고 다루게 해요. 켜고 저장한
            뒤 새로고침하면 [설정 › 권한 관리] 화면이 보여요. 지금의 관리자는 켜든 끄든 모든 화면을
            그대로 써요. 끄면 맡긴 운영자 모두의 접근이 바로 막혀요(맡긴 기록은 남아요). 앱에는
            바뀌는 것이 없어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="배정 운영자 접근 켜기"
            checked={form.accessGrants.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, accessGrants: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── AI 필수 동의 (2026-10-29 대표 결정) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-ai-consent">
        <div>
          <Text variant="heading">AI 필수 동의</Text>
          <Text variant="secondary" size="sm">
            AI 기능(생성·AI 수정·사진·맛 예측·번역·비교 제품 찾기·견적 요청서)은 지금 안내문에
            동의한 사람만 쓰게 해요. 동의 기록(판·시각·고른 곳)은 계정, 손님은 기기마다 서버에
            남아요. 둘 다 기본은 꺼짐이에요. 켜는 순서: ① 서버 기록을 쓰는 새 앱이 스토어에 나간 뒤
            첫 스위치를 켜요 — 새 앱은 AI 기능 앞에서 다시 동의를 받고, 서버는 새 앱의 미동의 요청과
            거부·철회한 사람의 요청을 거절해요. 옛 앱(1.0.7 까지)은 기기에 남긴 동의로 지금처럼
            써요. ② 새 버전 안내의 최소 버전을 새 앱으로 올린 뒤에 두 번째 스위치를 켜요 — 그러면
            서버 기록이 없는 옛 앱 요청도 거절해 전원 재동의가 끝나요. 앱은 AI 기능을 쓰기 직전에 새
            값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="AI 필수 동의 켜기 (새 앱 재동의 + 서버 거절)"
            checked={form.aiConsent.required}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, aiConsent: { ...f.aiConsent, required: checked } }))
            }
          />
          <Switch
            label="옛 앱도 서버 동의 기록이 있어야 AI 사용 (최소 버전을 올린 뒤에만)"
            checked={form.aiConsent.legacyAppsRequired}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({
                ...f,
                aiConsent: { ...f.aiConsent, legacyAppsRequired: checked },
              }))
            }
          />
        </Surface>
      </section>

      {/* ── 생성 팩 (로드맵 9위) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-generation-pack">
        <div>
          <Text variant="heading">생성 10회 팩</Text>
          <Text variant="secondary" size="sm">
            Pro 회원이 월 생성을 다 써서 결제 화면이 열리면 '생성 10회 팩' 구매 카드를 보여요. 산
            팩은 Pro 인 동안 월 한도를 다 쓴 뒤 자동으로 쓰이고, 스토어 환불이 오면 남은 횟수를
            회수해요. 1.0.8 이상 앱이 다음 실행 때 읽어요. 끄면 카드만 숨고 이미 산 팩은 그대로
            쓰여요.
          </Text>
        </div>
        <Banner
          variant="alert"
          size="sm"
          title="스토어 상품 generation_pack_10 을 만들고 RevenueCat 에 연결한 뒤 켜세요."
          description="상품이 없으면 카드에 '가격을 불러오지 못했어요'가 떠요. 순서는 저장소 docs/간편로그인-생성팩-켜기.md 에 있어요."
        />
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="생성 팩 구매 카드 보이기"
            checked={form.generationPack.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, generationPack: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 전문가 검수 (로드맵 매출 8위, 2026-10-29) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-expert-review">
        <div>
          <Text variant="heading">전문가 검수</Text>
          <Text variant="secondary" size="sm">
            결과 화면에 '전문가 검수 요청'을 보이고 검수권을 팔아요. 새 앱(이 기능을 아는 판)에서,
            스토어에 검수권 상품(expert_review_credit)이 있을 때만 보여요. 상품을 만들기 전에는 켜도
            아무에게도 보이지 않아요. 꺼도 이미 들어온 요청은 [전문가 검수] 대기열에서 끝까지
            처리하고, 기한 반려도 그대로 돌아요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="전문가 검수 요청 받기"
            checked={form.expertReview.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, expertReview: { ...f.expertReview, enabled: checked } }))
            }
          />
          <Input
            label="처리 기한 (일)"
            description="요청 뒤 이 날수 안에 아무도 맡지 않으면 서버가 반려하고 검수권을 돌려줘요. 1~30일"
            type="number"
            min={1}
            max={30}
            value={String(form.expertReview.slaDays)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({
                ...f,
                expertReview: {
                  ...f.expertReview,
                  slaDays: clamp(Number(e.target.value) || 1, 1, 30),
                },
              }))
            }
          />
        </Surface>
      </section>

      {/* ── 생성 횟수 초기화 안내 (2026-10-30) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-generation-reset-reminder">
        <div>
          <Text variant="heading">생성 횟수 초기화 안내</Text>
          <Text variant="secondary" size="sm">
            생성 횟수(월 한도와 생성 팩)는 이용 기간마다 초기화되고 남은 횟수는 이월되지 않아요.
            켜면 기간이 3일 안에 끝나고 남은 생성이 있는 유료 회원에게 기간마다 한 번 푸시와 알림
            탭으로 알려요. 이용 안내라 광고 수신 동의와 상관없이 가고, 밤(21~08시)에는 보내지
            않아요. 앱 업데이트 없이 다음 정각부터 적용돼요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="초기화 3일 전 안내 보내기"
            checked={form.generationResetReminder.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, generationResetReminder: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── 결과 화면 업셀·제품 사양서 (2026-10-29) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-result-upsell">
        <div>
          <Text variant="heading">결과 화면 업셀 · 제품 사양서</Text>
          <Text variant="secondary" size="sm">
            업셀을 켜면 결과 맨 아래 '산출물' 칸에 '다음 플랜이면 되는 것' 한 줄과 [플랜 비교]가 늘
            보이고, 결과를 나갈 때의 결제 화면이 밀어서 닫기·안드로이드 뒤로 가기에도 뜨며, 남은
            생성이 {AUTO_PAYWALL_LIMITS.near_limit.remainingAtMost}회 이하일 때도 떠요. 자동으로
            뜨는 결제 화면은 {AUTO_PAYWALL_GAP_HOURS}시간에 한 번, 한도 직전은{' '}
            {AUTO_PAYWALL_LIMITS.near_limit.cooldownHours}시간 간격으로 최대{' '}
            {AUTO_PAYWALL_LIMITS.near_limit.maxShows}번까지예요(앱 기기 기준). 사양서를 켜면 같은
            칸에 [사양서] 버튼이 생겨 제품 정보, 배합, 공정, 원가, 표시 사항을 한 장 그림으로 사진
            앱에 저장해요. 쓸 수 있는 플랜은 위 '플랜별 한도'의 제품 사양서 칸이고, 잠긴 플랜이
            누르면 결제 화면이 떠요. 둘 다 기본 꺼짐이고 새 앱에만 보여요. 앱은 다음 실행 때 새 값을
            읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="결과 화면 업셀 켜기"
            checked={form.resultUpsell.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, resultUpsell: { enabled: checked } }))
            }
          />
          <Switch
            label="제품 사양서 버튼 보이기"
            checked={form.specSheet.enabled}
            onCheckedChange={(checked: boolean) =>
              setForm((f) => ({ ...f, specSheet: { enabled: checked } }))
            }
          />
        </Surface>
      </section>

      {/* ── B2B 광고 (대표 결정 4) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-b2b-ads">
        <div>
          <Text variant="heading">B2B 광고</Text>
          <Text variant="secondary" size="sm">
            결과 화면 재료 줄의 원료 대체·포장재·업소용 받는 곳 카드, 견적 비교의 추천 공장 칸, 생성
            대기 화면 아래 광고 한 칸, 메뉴 결과의 업소용 시세 정보를 켜요. 광고 카드는 [수익 › 광고
            관리]에서 승인한 것만 나가고, 승인된 광고가 없으면 앱에는 칸이 생기지 않아요.
            &lsquo;광고&rsquo; 표시는 늘 붙어요. 앱 업데이트 없이 다음 화면부터 적용돼요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="B2B 광고 켜기"
            checked={form.b2bAds.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, b2bAds: { ...f.b2bAds, enabled } }))
            }
          />
          <div className="flex flex-wrap items-end gap-6">
            <div className="w-48">
              <Input
                label="화면당 광고 수"
                description="1~2"
                type="number"
                min={1}
                max={2}
                value={String(form.b2bAds.perScreenMax)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    b2bAds: { ...f.b2bAds, perScreenMax: clamp(Number(e.target.value) || 1, 1, 2) },
                  }))
                }
              />
            </div>
            <div className="w-48">
              <Input
                label="한 사람 하루 상한 (자리마다)"
                type="number"
                min={0}
                max={50}
                value={String(form.b2bAds.dailyCapPerViewer)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    b2bAds: {
                      ...f.b2bAds,
                      dailyCapPerViewer: clamp(Number(e.target.value) || 0, 0, 50),
                    },
                  }))
                }
              />
            </div>
            <div className="w-48">
              <Input
                label="같은 광고 하루 상한"
                type="number"
                min={0}
                max={20}
                value={String(form.b2bAds.dailyCapPerAd)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    b2bAds: {
                      ...f.b2bAds,
                      dailyCapPerAd: clamp(Number(e.target.value) || 0, 0, 20),
                    },
                  }))
                }
              />
            </div>
          </div>
          <Switch
            label="Pro 에게 결과 화면 광고 보이기"
            checked={form.b2bAds.proSeesResultAds}
            onCheckedChange={(proSeesResultAds: boolean) =>
              setForm((f) => ({ ...f, b2bAds: { ...f.b2bAds, proSeesResultAds } }))
            }
          />
          <Switch
            label="Pro 에게 생성 대기 광고 보이기 (대표 미정 · 기본 숨김)"
            checked={form.b2bAds.proSeesWaitAds}
            onCheckedChange={(proSeesWaitAds: boolean) =>
              setForm((f) => ({ ...f, b2bAds: { ...f.b2bAds, proSeesWaitAds } }))
            }
          />
        </Surface>
      </section>

      {/* ── 클릭식 맛 고르기 (2026-10-30 대표 결정 1) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-taste-axes">
        <div>
          <Text variant="heading">클릭식 맛 고르기</Text>
          <Text variant="secondary" size="sm">
            만들기 화면의 맛 칩 대신 카테고리별 맛 막대(맵기·짠맛·단맛 등)를 보여 주고, 결과 화면에
            '이 제품의 맛' 카드와 '맛 다시 맞추기'(AI 수정 1회)를 보여요. 세부 축과 수치 목표를
            플랜마다 얼마나 열지는 위 플랜별 한도의 '맛 세부 축'·'맛 수치 목표'가 정해요. 끄면
            지금의 맛 칩 화면 그대로이고, 서버도 맛 값을 받지 않아요. 이 카드를 모르는 옛 앱에는
            보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="클릭식 맛 고르기 켜기"
            checked={form.tasteAxes.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, tasteAxes: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── '더 만들어도 남을까' (2026-10-30 대표 Top5) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-scale-check">
        <div>
          <Text variant="heading">더 만들어도 남을까 · 검증 단계</Text>
          <Text variant="secondary" size="sm">
            제품 검토 리포트 맨 위에 규모별 손익(100·500·1,000개·MOQ)과 같은 품질로 만들려면 확인할
            조건을 한 장으로 보여 주고, 결과 화면·내 제품 목록에 검증 단계(아이디어~반복 생산)를
            보여요. 원가 시나리오는 서버가 계산해 배합 버전마다 저장해요. 숫자마다 근거(직접
            입력·계산·AI 추정·시식 기록·공급사 견적·미확인)를 붙여요. 끄면 지금 화면 그대로예요.
            앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="더 만들어도 남을까 켜기"
            checked={form.scaleCheck.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, scaleCheck: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── 견적 상태 확장 (2026-10-30 Top10 #5) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-quote-statuses">
        <div>
          <Text variant="heading">견적 진행 단계</Text>
          <Text variant="secondary" size="sm">
            견적 상세에 '공장 확인 중'·'추가 정보 요청' 버튼을 보이고, 앱 견적 상세에도 그 단계
            안내를 보여요. 끄면 예전처럼 접수 → 제안 도착 → 마감만 있어요. 상태 이력은 이 스위치와
            상관없이 견적 상세 아래에 쌓여요. 이 단계를 모르는 옛 앱은 '접수'로 봐요. 앱은 다음 실행
            때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="견적 진행 단계 켜기"
            checked={form.quoteStatuses.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, quoteStatuses: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── 아이폰 실시간 현황·홈 위젯 (2026-10-30) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-live-activities">
        <div>
          <Text variant="heading">아이폰 실시간 현황·홈 위젯</Text>
          <Text variant="secondary" size="sm">
            실시간 현황: 생성 단계·견적 상태·샘플 진행을 다이내믹 아일랜드와 잠금화면에 보내고,
            견적이 도착하면 앱이 꺼져 있어도 시작해요. 홈 위젯: 진행 중 제품·새 견적 수·남은 생성
            횟수를 홈 화면과 시리에 보여요. 둘 다 1.0.9 빌드부터 보이고, 옛 앱은 아무것도 바뀌지
            않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="실시간 현황 켜기"
            checked={form.liveActivities.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, liveActivities: { enabled } }))
            }
          />
          <Switch
            label="홈 위젯·시리 켜기"
            checked={form.homeWidgets.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, homeWidgets: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── 코스·테이스팅 (2026-11-01 대표 확정 명세) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-course-tasting">
        <div>
          <Text variant="heading">코스·테이스팅</Text>
          <Text variant="secondary" size="sm">
            제품 만들기 첫 분류 화면에 가공식품·메뉴 옆 셋째 칸 '코스·테이스팅'을 보여요. 고르면
            코스 역할 10개(Amuse·한 입부터 Cheese course까지)와 스타일·기법을 묻고, 코스 한 자리의
            접시로 만들어요. 기본은 켬이에요. 꺼도 이미 만든 코스 제품은 그대로 열리고, 고르던
            생성도 그대로 받아요. 1.0.9 빌드부터 보이고 옛 앱은 아무것도 바뀌지 않아요. 앱은 다음
            실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="코스·테이스팅 칸 보이기"
            checked={form.courseTasting.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, courseTasting: { enabled } }))
            }
          />
        </Surface>
      </section>

      {/* ── 인증 준비도 가이드 (2026-11-01, 기본 꺼짐) ── */}
      <section className="flex flex-col gap-3" data-testid="settings-cert-readiness">
        <div>
          <Text variant="heading">인증 준비도 가이드</Text>
          <Text variant="secondary" size="sm">
            만들기 화면에 '판매·인증 목표' 칩(할랄·해썹)을 보이고, 고른 기준에 맞춰 배합·공정을 짜게
            한 뒤 결과 화면에 준비도 카드(설계에 반영됨·고칠 것·공장·서류로 확인할 것)를 보여요.
            앱은 인증 주체가 아니고 인증 여부를 말하지 않아요. 기본은 꺼짐이에요: 내부 계정으로
            확인하고 문구·약관 법률 검토를 받은 뒤 켜세요. 꺼도 이미 만든 제품의 준비도는 지우지
            않고, 앱은 카드를 숨겨요. 플랜 제한은 없어요(할랄·해썹과 고칠 것은 모든 플랜). 공유
            페이지·커뮤니티에는 보이지 않아요. 앱은 다음 실행 때 새 값을 읽어요.
          </Text>
        </div>
        <Surface className="flex flex-col gap-5 rounded-lg p-6">
          <Switch
            label="인증 준비도 켜기"
            checked={form.certReadiness.enabled}
            onCheckedChange={(enabled: boolean) =>
              setForm((f) => ({ ...f, certReadiness: { ...f.certReadiness, enabled } }))
            }
          />
          {CERT_TARGETS_AVAILABLE.map((target) => (
            <Switch
              key={target}
              label={
                target === 'halal' ? '할랄 목표 열기' : '해썹(HACCP) 목표 열기 (국내 판매 제품만)'
              }
              checked={form.certReadiness.targets.includes(target)}
              onCheckedChange={(on: boolean) =>
                setForm((f) => ({
                  ...f,
                  certReadiness: {
                    ...f.certReadiness,
                    targets: on
                      ? [...new Set([...f.certReadiness.targets, target])]
                      : f.certReadiness.targets.filter((t) => t !== target),
                  },
                }))
              }
            />
          ))}
        </Surface>
      </section>

      <ConfirmDialog
        open={confirmOn}
        title="완성 사례를 켜고 저장할까요?"
        description={
          <>
            {sampleCasesTurningOn.map((r) => (
              <span key={r.key} className="block">
                <b>{r.label}</b>: {r.onNote}
              </span>
            ))}
            <span className="mt-2 block">다른 바꾼 값도 함께 저장돼요.</span>
          </>
        }
        confirmLabel="켜고 저장"
        busy={busy}
        onConfirm={() => void save()}
        onClose={() => !busy && setConfirmOn(false)}
      />
    </div>
  )
}
